import { ReactElement, ReactNode, useState } from 'react';

import { Button } from '@components/button';
import { IconProps } from '@components/icons';
import { Modal, hideModal, useModal } from '@components/modal';
import { ModalOptions } from '@components/modal/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

export const useUnsavedChangesModal = (options?: ModalOptions) =>
  useModal(UnsavedChangesPromptModal, options);

export type UnsavedChangesPromptModalProps = {
  title?: string;
  titleIcon?: ReactElement<IconProps>;
  description?: string;
  saveText?: string;
  discardText?: string;
  isSaveDisabled?: boolean;
  children?: ReactNode;
  onSave(): void | Promise<void>;
  onDiscard?(): void | Promise<void>;
};

const UnsavedChangesPromptModal = ({
  title,
  titleIcon,
  description,
  saveText,
  discardText,
  isSaveDisabled,
  children,
  onSave,
  onDiscard,
}: UnsavedChangesPromptModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [isLoading, setIsLoading] = useState(false);

  const handleAction = async (callback?: () => void | Promise<void>) => {
    setIsLoading(true);
    try {
      await callback?.();
      hideModal();
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={title ?? t('unsavedChanges')} icon={titleIcon} />
      <Modal.Content>
        <Stack space={6}>
          <Text text={description ?? t('areYouSure')} />
          {children}
          <Stack orientation="horizontal" justify="space-between">
            <Button
              variant="primary-red"
              isDisabled={isLoading}
              onClick={() => handleAction(onDiscard)}
              text={discardText ?? t('discard')}
            />
            <Stack orientation="horizontal" space={2} justify="flex-end">
              <Button
                variant="secondary"
                isDisabled={isLoading}
                onClick={hideModal}
                text={t('core.cancel')}
              />
              <Button
                variant="primary-blue"
                isDisabled={isLoading || isSaveDisabled}
                onClick={() => handleAction(onSave)}
                text={saveText ?? t('core.save')}
              />
            </Stack>
          </Stack>
        </Stack>
      </Modal.Content>
    </Modal>
  );
};

const locale = {
  en: {
    unsavedChanges: 'Unsaved changes',
    areYouSure: 'You have unsaved changes.\nWould you like to save or discard them?',
    discard: 'Discard',
  },
};
